import CsiFormData from "csi/action/FormData";
import def from "csi/def";
import { Field } from "csi/def/Field";
import { View } from "csi/def/View";
import { FormWidget } from "csi/form/Widget";
import csi from "csi/main";
import reflection from "csi/reflection";
import WorkingCache from "csi/state/WorkingCache";
import { ICache, cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ViewWidget } from "csi/view/Widget";
import xml from "csi/xml";
import { SaveDialogProps } from "designSystem/components/dialog/SaveDialog";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { createContext, useContext } from "react";
import { C8Node } from "./../../../c8-platform/type/node";
import Sourcing from "./../../main";
import { getSRMSourcesAllocation, getSourcesAllocation } from "./NewAllocatedSupplierRequestService";

export interface AllocationRecordItem {
	/** string unique identifier */
	id: string;

	/** string label to be shown */
	isExisting?: boolean;

	/** name which would show up as the visual text */
	name?: string;

	children?: AllocationRecordItem[];
}
export class NewAllocatedSupplierRequestStore extends ReactionStore {
	private readonly cache: ICache = new WorkingCache();

	/** action formdata */
	#formData: CsiFormData;

	/** callback to get save dialog props */
	getReactSaveDialogProps: (view: ViewWidget) => Partial<SaveDialogProps>;

	#isFromHierarchy: boolean;

	#parentNode: C8Node;

	#srMaster: C8Node;

	// TODO: check for refinement base on this comment https://bitbucket.org/centricsoftware/c8/pull-requests/54306#comment-458411715
	@observable.ref formWidget: FormWidget;

	// save dialog props
	@observable.ref saveDialogProps: Partial<SaveDialogProps>;

	protected readonly locale: ILocaleProvider;

	readonly nodeType: string = "SupplierRequest";

	readonly titleRes: string = "NewAllocatedSupplierRequests";

	readonly formId: string = def.DEFAULT_FORM_ID;

	/** Form fields name */
	readonly SRM_PRODUCTS: string = "SRMProducts";
	readonly SR_SUPPLIERS: string = "SRSuppliers";
	readonly SR_SUPPLIER_FACTORIES: string = "SRSupplierFactories";
	readonly SR_MASTER: string = "SRMaster";
	readonly CREATE_OPTION: string = "CreateOption";
	readonly SR_SUBTYPE: string = "Subtype";
	readonly UPDATE_EXISTING_SUPPLIER_REQUESTS: string = "UpdateExistingSupplierRequests";

	/** full Allocation list*/
	allocation: AllocationRecordItem[] = [];

	/** To identify if the allocation list is fetched */
	unAllocatedGroup: AllocationRecordItem = null;

	/** To identify if the allocation list is fetched */
	isExistingAllocationFetched: boolean = false;

	/** product field name */
	productField: string = "Styles";

	/** Products added in Master Locale */
	productsAddedInMasterRes: string;

	/** number of SRM to be created */
	@observable masterToBeCreated: number = 0;

	/** To identify if Soure is Supplier or Factory */
	@observable sourceType: string = "";

	/** number of Styles to be added to the Master */
	@observable productsToBeAddedInMaster: number = 0;

	/** number of Supplier Request to be created */
	@observable srToBeCreated: number = 0;

	@observable.ref selectedSources: AllocationRecordItem[] = [];

	constructor(
		cache: ICache,
		getReactSaveDialogProps: (view: ViewWidget) => Partial<SaveDialogProps>,
		locale: ILocaleProvider,
		formData: CsiFormData,
		parentNode: C8Node,
		isFromHierarchy: boolean,
		srMaster: C8Node,
		productField: string,
	) {
		super();
		makeObservable(this);

		this.cache = cache;
		this.getReactSaveDialogProps = getReactSaveDialogProps;
		this.locale = locale;
		this.#formData = formData;
		this.#parentNode = parentNode;
		this.#isFromHierarchy = isFromHierarchy;
		this.#srMaster = srMaster;
		this.productField = productField;
		this.productsAddedInMasterRes = productField === "Styles" ? "StylesAddedInMaster" : "MaterialsAddedInMaster";
	}

	/**
	 * Set FormView Params and pass isFromHierarchy to NewAllocatedSupplierRequestsForm controller 
	 * and pass the SRM node if the action is inside a SRM.
	 */
	get viewParams(): Object {
		const { nodeType } = this;
		
		const rc = reflection.getClass(nodeType);
		const defView = def.getClassViewDef(rc, this.formId) as View;
		defView.classController = "Sourcing.NewAllocatedSupplierRequestsForm";
		defView.setFormData(this.#formData);
		
		const formInfo = {
			contextUrl: this.#parentNode?.$URL,
			nodeType: nodeType,
		};
		defView.classControllerParams = {
			isFromHierarchy: this.#isFromHierarchy,
			srMaster: this.#srMaster,
		};
		
		return {
			bPopup: true,
			defView: defView,
			formInfo: formInfo,
			cacheView: this.cache,
			onAfterChangeField: this.onAfterChangeField,
		};
	}

	/** Get the product node when the context is Product */
	private get product(): C8Node {
		const rc = reflection.getClass(this.#parentNode?.$Type);
		if (rc.isDerivedFrom("Product")) {
			return this.#parentNode;
		}
		return null;
	};

	/** Get the source field name based on product or the subtype */
	private get  sourceFieldName(): string {
		const node = this.product || this.cache
			.get(this.formWidget.getField(this.SR_SUBTYPE)
				.getValue());
		if (!node) {
			return null;
		}
		const isFactoryDriven = Sourcing.isFDDEnabled(node, this.formWidget?.getCache());
		return isFactoryDriven ? this.SR_SUPPLIER_FACTORIES : this.SR_SUPPLIERS;
	}

	private get master(): C8Node {
		return this.#srMaster || this.cache.get(this.formWidget.getFieldValue(this.SR_MASTER));
	}

	/** Set productsToBeAddedInMaster based on the styles selected and master SRMProducts allocation */
	private setProductsTobeAddedAmount(): void {
		const { master, productField } = this;
		const products = this.formWidget.getField(productField)
			.getValue();
		if (!products) {
			this.productsToBeAddedInMaster = 0;
		} else if (products && master) {
			this.productsToBeAddedInMaster = products.filter((product: cnl) => !master.SRMProducts.includes(product)).length;
		} else {
			this.productsToBeAddedInMaster = products.length;
		}
	}

	/** Set srToBeCreated based on the sources selected */
	private setSRToBeCreatedAmount(): void {
		const sourceField = this.formWidget.getField(this.sourceFieldName);
		runInAction(() => {
			if (!sourceField) {
				this.sourceType = "";
			} else {
				this.sourceType = sourceField.m_id == this.SR_SUPPLIER_FACTORIES ? "Factory" : "Supplier";
			}
			this.srToBeCreated = sourceField ? sourceField.getValue().length : 0;
		});
	}

	/** Get the message to show for unallocated products group */
	get unallocatedProductsListTitle(): string {
		return this.locale.resource("UnallocatedProducts");
	}

	/** Get the source plural label based on the sourceType(Supplier or Factory) */
	@computed get sourcePluralLabel(): string {
		return this.locale.type(this.sourceType, true);
	}

	/** Get the products selected on the form */
	private get products(): cnl[] {
		return this.formWidget.getField(this.productField)
			.getValue();
	}

	/** Get the sources selected on the form */
	private get sources(): cnl[] {
		return this.formWidget
			.getFieldValue(this.sourceFieldName)
			?.filter((sourceURL) => xml.isValidURL(sourceURL));
	}

	/** Update selectedSources sources allocation by supplies/Factories and products seleted. */
	private async updateSourcesAllocation(): Promise<void> {
		const updateExisting = this.formWidget.getField(this.UPDATE_EXISTING_SUPPLIER_REQUESTS).getValue();
		if ((this.product || this.products?.length > 0) && this.sources?.length > 0) {
			let allocation = await getSourcesAllocation(this.sources, this.products);
			this.setUnallocatedGroup(allocation);
			this.isExistingAllocationFetched = false;
			if (this.master && updateExisting) {
				allocation = await this.addExistingAllocation(allocation, this.products);
			}
			runInAction(() => {
				this.allocation = allocation;
				this.manageExistingAllocation(this.formWidget.getField(this.UPDATE_EXISTING_SUPPLIER_REQUESTS).getValue());
			});
		} else {
			runInAction(() => {
				this.selectedSources = [];
			});
		}		
	}

	/** Set unallocated group base on current allocation */
	setUnallocatedGroup(allocation: AllocationRecordItem[]): void {
		this.unAllocatedGroup = { ... allocation.find((item) => item.id === csi.EMPTY_URL) };
	}

	/** Add existing allocation to the allocation list */
	private async addExistingAllocation(allocation: AllocationRecordItem[], products: cnl[]): Promise<AllocationRecordItem[]> {
		let existingAllocation = await getSRMSourcesAllocation(this.master.$URL, products);
		
		existingAllocation = existingAllocation
			.map((item: AllocationRecordItem) => {
				item.isExisting = true;
				return item;});
		if (xml.isValidURL(allocation.slice(-1)["id"])) {
			allocation.push(...existingAllocation);
		} else {
			allocation.splice(allocation.length - 1, 0, ...existingAllocation);
		}
		this.filterUnallocatedGroupByExistingProduct(allocation);
		this.isExistingAllocationFetched = true;
		return allocation;
	}

	/**
	 * Filter unallocated group based on existing allocation
	 * @param existingAllocation 
	 * @param allocation 
	 */
	private filterUnallocatedGroupByExistingProduct(allocation: AllocationRecordItem[]): void {
		const existingProducts = new Set(allocation.filter((group) => group.children && group.id !== csi.EMPTY_URL)
			.flatMap((group) => group.children)
			.map((product) => product.id));
		const unallocatedGroup = allocation.find((item) => item.id === csi.EMPTY_URL);
		if (unallocatedGroup) {
			unallocatedGroup.children = unallocatedGroup.children.filter((product) => !existingProducts.has(product.id));
		}
	}

	/** Set values for overview panel */
	@action onAfterChangeField = (field: Field): void => {
		const { CREATE_OPTION, SR_SUPPLIERS, SR_SUPPLIER_FACTORIES, productField, SR_MASTER, SR_SUBTYPE, UPDATE_EXISTING_SUPPLIER_REQUESTS } = this;
		switch (field.m_id) {
			case CREATE_OPTION:
				this.masterToBeCreated = field.getValue() == 0 ? 1 : 0;
				break;
			case SR_SUPPLIERS:
			case SR_SUPPLIER_FACTORIES:
				this.setSRToBeCreatedAmount();
				this.updateSourcesAllocation();
				break;
			case productField:
			case SR_MASTER:
			case SR_SUBTYPE:
				this.setProductsTobeAddedAmount();
				this.setSRToBeCreatedAmount();
				this.updateSourcesAllocation();
				break;
			case UPDATE_EXISTING_SUPPLIER_REQUESTS:
				this.manageExistingAllocation(field.getValue());
				break;
		}
	};

	/** Set selectedSources based on allocation and Update Existing SRs flag if Products and Sources selected */
	@action private async  manageExistingAllocation(updateExisting: boolean): Promise<void> {
		const { allocation } = this;
		if (!(this.product || this.products?.length > 0) || this.sources?.length == 0) {
			return;
		}
		if (updateExisting) {
			if (!this.isExistingAllocationFetched && (this.product || this.products?.length > 0) && this.sources?.length > 0 && this.master) {
				const allocationWithExisting = await this.addExistingAllocation(allocation, this.products);
				this.allocation = allocationWithExisting;
			}
			runInAction(() => {
				this.filterUnallocatedGroupByExistingProduct(allocation);
				this.selectedSources = allocation;
			});
		} else {
			runInAction(() => {
				this.resetUnallocatedGroup(allocation);
				this.selectedSources = allocation.filter((item: AllocationRecordItem) => !item.isExisting);
			});
		}
	};

	/** Reset unallocated group */
	resetUnallocatedGroup(allocation: AllocationRecordItem[]): void {
		const unallocatedGroup = allocation.find((item) => item.id === csi.EMPTY_URL);
		if (unallocatedGroup) {
			unallocatedGroup.children = this.unAllocatedGroup.children;
		}
	}

	/** Get the message to show for the form */
	get formInfoMsg (): string {
		return this.locale.resource("NewAllocatedSupplierRequestInfo", [this.locale.type(this.productField.slice(0, -1))]);
	}

	/** Set formWidget, saveDialogProps, masterToBeCreated, and srToBeCreated when view is loaded */
	@action onViewLoad = (view: ViewWidget): void => {
		this.formWidget = view as FormWidget;
		this.saveDialogProps = this.getReactSaveDialogProps(view);
		this.masterToBeCreated = this.#srMaster ? 0 : 1;
		this.setSRToBeCreatedAmount();
	};
}

export const NewAllocatedSupplierRequestStoreContext = createContext<NewAllocatedSupplierRequestStore>(undefined);
export const useNewAllocatedSupplierRequestStore = (): NewAllocatedSupplierRequestStore => useContext(NewAllocatedSupplierRequestStoreContext);